import React, { type RefObject } from 'react';
import dayjs from 'dayjs';
import type { TempRange } from '../types';
import TimezoneSelector from './TimezoneSelector';

interface CustomRangeInputsProps {
  tempRange: TempRange;
  onRangeChange: (range: TempRange) => void;
  inputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
  onCancel: () => void;
  showTimezone?: boolean;
  timezone: string;
  timezoneOpen: boolean;
  onTimezoneToggle: () => void;
  onTimezoneChange: (tz: string) => void;
  timezoneRef: RefObject<HTMLDivElement | null>;
}

const CustomRangeInputs: React.FC<CustomRangeInputsProps> = ({
  tempRange,
  onRangeChange,
  inputValue,
  onInputChange,
  onApply,
  onCancel,
  showTimezone,
  timezone,
  timezoneOpen,
  onTimezoneToggle,
  onTimezoneChange,
  timezoneRef,
}) => {
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = dayjs(e.target.value);
    if (date.isValid()) {
      const newRange = { ...tempRange, from: date };
      onRangeChange(newRange);
      if (tempRange.to) {
        onInputChange({
          target: { value: `${date.format('YYYY-MM-DD HH:mm')} to ${tempRange.to.format('YYYY-MM-DD HH:mm')}` },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = dayjs(e.target.value);
    if (date.isValid()) {
      const newRange = { ...tempRange, to: date };
      onRangeChange(newRange);
      if (tempRange.from) {
        onInputChange({
          target: { value: `${tempRange.from.format('YYYY-MM-DD HH:mm')} to ${date.format('YYYY-MM-DD HH:mm')}` },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  return (
    <div className="w-80 p-4">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            type="datetime-local"
            value={tempRange.from ? tempRange.from.format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleFromDateChange}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            type="datetime-local"
            value={tempRange.to ? tempRange.to.format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleToDateChange}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Or enter expression</label>
          <input
            type="text"
            value={inputValue}
            onChange={onInputChange}
            placeholder="e.g. now-1h to now"
            className="w-full px-2 py-1 border rounded text-sm"
          />
          <div className="mt-2 text-xs text-gray-500">
            Examples: now-1h, now-1d, 2024-01-01, 2024-01-01 15:30:00
          </div>
        </div>

        {showTimezone && (
          <TimezoneSelector
            value={timezone}
            onChange={onTimezoneChange}
            isOpen={timezoneOpen}
            onToggle={onTimezoneToggle}
            containerRef={timezoneRef}
          />
        )}

        <div className="flex gap-2 pt-4">
          <button
            onClick={onApply}
            disabled={!tempRange.from || !tempRange.to}
            className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 border rounded text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomRangeInputs;
